import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFacebook,
  faInstagram,
  faTwitter,
  faTiktok,
  faLinkedin,
  faGoogle,
  faMailchimp,
  faWhatsapp,
  faGoogleDrive,
  faYoutube,
} from "@fortawesome/free-brands-svg-icons";
import {
  faEnvelope,
  faBuilding,
  faBoxOpen,
  faSyncAlt,
  faShoppingCart,
  faUsers,
  faBullhorn,
  faRobot,
  faBriefcase,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons";
import { useIntl } from "react-intl";
import cn from "classnames";
import { PanelWembii } from "../../PanelWembii";
import SyncAccount from "../../SyncAccount/SyncAccount";
import { useMeCore } from "../../../../cores/me";
import { useFacebookCore } from "../../../../cores/facebook";
import { useInstagramCore } from "../../../../cores/instagram";
import { useLinkedInCore } from "../../../../cores/linkedIn";
import { useLinkedInBusinessCore } from "../../../../cores/linkedInBusiness";
import { useGoogleCore } from "../../../../cores/google";
import { useFacebookAdsCore } from "../../../../cores/facebookAds";
import { useGmailCore } from "../../../../cores/gmail";
import { useOffice365Core } from "../../../../cores/office365";
import { useSmtpCore } from "../../../../cores/smtp";
import { useWooCommerceCore } from "../../../../cores/wooCommerce";
import { useMercadolibreCore } from "../../../../cores/mercadolibre";
import { useJumpsellerCore } from "../../../../cores/jumpseller";
import { useMailchimpCore } from "../../../../cores/mailchimp";
import { useOpenAiCore } from "../../../../cores/openAi";
import { useWhatsappCore } from "../../../../cores/whatsapp";
import { useInstagramLoginCore } from "../../../../cores/instagramLogin";
import { SocialProvider } from "../../../../cores/socialPost/interfaces/socialProvider";
import { SocialProviderId } from "../../../../cores/socialPost/config";
import { FacebookIcon } from "../Facebook/FacebookIcon";
import _ from "lodash";
import { FacebookAdsIcon } from "../FacebookAds/FacebookAdsIcon";
import { GmailIcon } from "../Gmail/GmailIcon";
import { InstagramIcon } from "../Instagram/InstagramIcon";
import { JumpsellerIcon } from "../Jumpseller/JumpsellerIcon";
import { LinkedInIcon } from "../LinkedIn/LinkedInIcon";
import { MailchimpIcon } from "../Mailchimp/MailchimpIcon";
import { MercadolibreIcon } from "../Mercadolibre/MercadolibreIcon";
import { Office365Icon } from "../Office365/Office365Icon";
import { OpenAiIcon } from "../OpenAi/OpenAiIcon";
import { SmtpIcon } from "../Smtp/SmtpIcon";
import { WhatsappIcon } from "../Whatsapp/WhatsappIcon";
import { WooCommerceIcon } from "../WooCommerce/WooCommerceIcon";
import { GooglenAnalyticsIcon } from "../GoogleAnalitycs/GoogleAnalitycsIcon";
import FacebookAuthButton from "../Facebook/FacebookAuthButton";
import InstagramConnectionSelector from "../InstagramLogin/InstagramConnectionSelector";
import LinkedInAuthButton from "../LinkedIn/LinkedInBindCard";
import LinkedInBusinessAuthButton from "../LinkedIn/LinkedInBusinessBindCard";
import FacebookAdsAuthButton from "../FacebookAds/FacebookAdsBindCard";
import GmailAuthButton from "../Gmail/GmailBindCard";
import GoogleAnalyticsAuthButton from "../GoogleAnalitycs/GoogleBindCard";
import JumpSellerAuthButton from "../Jumpseller/JumpsellerBindCard";
import MailchimpAuthButton from "../Mailchimp/MailchimpBindCard";
import MercadolibreAuthButton from "../Mercadolibre/MercadolibreBindCard";
import Office365AuthButton from "../Office365/Office365BindCard";
import OpenAiAuthButton from "../OpenAi/OpenAiBindCard";
import SmtpAuthButton from "../Smtp/SmtpBindCard";
import WhatsappAuthButton from "../Whatsapp/WhatsappBindCard";
import WooCommerceAuthButton from "../WooCommerce/WooCommerceBindCard";
import { useGlobalCore } from "../../../../cores/globals";
import { isYoutubePostingEnabled } from "../../../../cores/config/featureFlags";
import {
  FACEBOOK_ADS_SYNC_LOADING,
  FACEBOOK_SYNC_LOADING,
  GMAIL_SYNC_LOADING,
  GOOGLE_SYNC_LOADING,
  INSTAGRAM_LOGIN_SYNC_LOADING,
  INSTAGRAM_LOGIN_GET_LOADING,
  INSTAGRAM_SYNC_LOADING,
  JUMPSELLER_SYNC_LOADING,
  LINKED_IN_BUSINESS_SYNC_LOADING,
  LINKED_IN_SYNC_LOADING,
  MAILCHIMP_SYNC_LOADING,
  MERCADOLIBRE_SYNC_LOADING,
  OFFICE365_ME_SYNC_LOADING,
  OFFICE365_SYNC_LOADING,
  OPENAI_SYNC_LOADING,
  SMTP_SYNC_LOADING,
  TIKTOK_SYNC_LOADING,
  WHATSAPP_SYNC_LOADING,
  WOO_COMMERCE_SYNC_LOADING,
  YOUTUBE_GET_LOADING,
  YOUTUBE_SYNC_LOADING,
} from "../../../../constants/loading.constant";
import { LoadingSpinnerWembii } from "../../LoadingSpinnerWembii";
import { daysRemainingFromToday } from "../../../utils/dateUtils";
import { LinkedInBusinessIcon } from "../LinkedIn/LinkedInBusinessIcon";
import TiktokAuthButton from "../Tiktok/TiktokBindCard";
import YoutubeAuthButton from "../Youtube/YoutubeBindCard";
import { useTiktokCore } from "../../../../cores/tiktok";
import { useYoutubeCore } from "../../../../cores/youtube";
import { TiktokIcon } from "../Tiktok/TiktokIcon";
import { YoutubeIcon } from "../Youtube/YoutubeIcon";
import { GoogleCalendarIcon } from "../GoogleCalendar/GoogleCalendarIcon";
import { useGoogleCalendarCore } from "../../../../cores/googleCalendar";
import ScopedIntegrationDetail from "./ScopedIntegrationDetail";

const DUAL_SCOPE_INTEGRATIONS = new Set([
  "Gmail",
  "Office365",
  "Smtp",
  "GoogleCalendar",
]);

interface CombinedSettings {
  [key: string]: { expiresIn: string | null } | null;
}
const SocialTable = () => {
  const intl = useIntl();
  const { company } = useMeCore();
  const { anyLoading } = useGlobalCore();
  const { settings: facebookSettings } = useFacebookCore();
  const { settings: facebookAdsSettings } = useFacebookAdsCore();
  const { settings: instagramSettings } = useInstagramCore();
  const { settings: instagramLoginSettings, get: getInstagramLogin } =
    useInstagramLoginCore();
  const { settings: linkedinSettings } = useLinkedInCore();
  const { settings: tikTokSettings } = useTiktokCore();
  const { settings: youtubeSettings, get: getYoutube } = useYoutubeCore();
  const { settings: linkedinBusinessSettings } = useLinkedInBusinessCore();
  const { settings: googleSettings } = useGoogleCore();
  const { settings: gmailSettings, userSettings: gmailUserSettings, get: getGmail } =
    useGmailCore();
  const {
    settings: officeSettings,
    userSettings: officeUserSettings,
    get: getOffice365,
  } = useOffice365Core();
  const { settings: smtpSettings, userSettings: smtpUserSettings, get: getSmtp } =
    useSmtpCore();
  const { settings: wooSettings } = useWooCommerceCore();
  const { settings: meliSettings } = useMercadolibreCore();
  const { settings: jumpsellerSettings } = useJumpsellerCore();
  const { settings: mailchimpSettings } = useMailchimpCore();
  const { settings: whatsappSettings } = useWhatsappCore();
  const { settings: openAiSettings } = useOpenAiCore();
  const { userSettings: googleCalendarSettings, get: getGoogleCalendar } =
    useGoogleCalendarCore();

  useEffect(() => {
    if (company?.id) {
      getInstagramLogin(String(company.id));
    }
  }, [company, getInstagramLogin]);

  const resolvedInstagramSettings = useMemo(() => {
    if (!_.isEmpty(instagramSettings)) return instagramSettings;
    if (!_.isEmpty(instagramLoginSettings?.token)) return instagramLoginSettings;
    return instagramSettings;
  }, [instagramSettings, instagramLoginSettings]);

  const combinedSettings: CombinedSettings = {
    Facebook: facebookSettings,
    [SocialProviderId.FacebookAds]: facebookAdsSettings,
    [SocialProviderId.Instagram]: resolvedInstagramSettings
      ? { expiresIn: resolvedInstagramSettings.expiresIn ?? null }
      : null,
    [SocialProviderId.LinkedIn]: linkedinSettings,
    [SocialProviderId.LinkedInBusiness]: linkedinBusinessSettings,
    [SocialProviderId.Google]: googleSettings,
    [SocialProviderId.Gmail]: gmailSettings,
    [SocialProviderId.Office365]: officeSettings,
    [SocialProviderId.Jumpseller]: jumpsellerSettings,
    [SocialProviderId.TikTok]: tikTokSettings,
    [SocialProviderId.YouTube]: youtubeSettings as {
      expiresIn: string | null;
    } | null,
  };
  const [selectedNetwork, setSelectedNetwork] = useState<string>("");
  const [integrationDetailExpanded, setIntegrationDetailExpanded] =
    useState(true);
  const integrationDetailRef = useRef<HTMLDivElement | null>(null);

  const [accounts, setAccounts] = useState<{
    [key: string]: SocialProvider[] | undefined | null;
  }>({});

  useEffect(() => {
    if (!company?.id) return;
    const id = String(company.id);
    getGmail(id);
    getGmail(id, true);
    getOffice365(id);
    getOffice365(id, true);
    getSmtp(id);
    getSmtp(id, true);
    getGoogleCalendar(id);
  }, [company, getGmail, getOffice365, getSmtp, getGoogleCalendar]);

  useEffect(() => {
    if (isYoutubePostingEnabled() && company?.id) {
      getYoutube(String(company.id));
    }
  }, [company, getYoutube]);

  const isValidSettings = (settings: any) => {
    if (settings === undefined || settings === null) {
      return false;
    }

    if (
      _.isObject(settings) &&
      _.keys(settings).length === 1 &&
      (settings as any).type
    ) {
      return false;
    }

    return !_.isEmpty(settings);
  };

  const resolveDualScopeSettings = (companySettings: any, personalSettings: any) => {
    if (isValidSettings(companySettings)) return companySettings;
    if (isValidSettings(personalSettings)) return personalSettings;
    return null;
  };

  const updateAccounts = (key: string, settings: any, icon: JSX.Element) => {
    return isValidSettings(settings)
      ? [
          {
            id: key,
            name: key,
            icon: icon,
            displayName: settings.name ?? "Cuenta no conectada",
            avatar: settings.avatarUrl,
          },
        ]
      : [];
  };
  const anyIsloading = anyLoading([
    FACEBOOK_ADS_SYNC_LOADING,
    LINKED_IN_BUSINESS_SYNC_LOADING,
    INSTAGRAM_LOGIN_SYNC_LOADING,
    INSTAGRAM_LOGIN_GET_LOADING,
    INSTAGRAM_SYNC_LOADING,
    LINKED_IN_SYNC_LOADING,
    FACEBOOK_SYNC_LOADING,
    FACEBOOK_ADS_SYNC_LOADING,
    GOOGLE_SYNC_LOADING,
    GMAIL_SYNC_LOADING,
    OFFICE365_SYNC_LOADING,
    OFFICE365_ME_SYNC_LOADING,
    SMTP_SYNC_LOADING,
    WHATSAPP_SYNC_LOADING,
    GMAIL_SYNC_LOADING,
    JUMPSELLER_SYNC_LOADING,
    MAILCHIMP_SYNC_LOADING,
    OPENAI_SYNC_LOADING,
    WOO_COMMERCE_SYNC_LOADING,
    MERCADOLIBRE_SYNC_LOADING,
    TIKTOK_SYNC_LOADING,
    YOUTUBE_GET_LOADING,
    YOUTUBE_SYNC_LOADING,
  ]);
  useEffect(() => {
    setAccounts((prevAccounts) => ({
      ...prevAccounts,
      Facebook: updateAccounts(
        SocialProviderId.Facebook,
        facebookSettings,
        <FacebookIcon size="5x" active={!_.isEmpty(facebookSettings)} />
      ),
      Instagram: updateAccounts(
        SocialProviderId.Instagram,
        resolvedInstagramSettings,
        <InstagramIcon size="5x" active={!_.isEmpty(resolvedInstagramSettings)} />
      ),
      LinkedIn: updateAccounts(
        SocialProviderId.LinkedIn,
        linkedinSettings,
        <LinkedInIcon size="5x" active={!_.isEmpty(linkedinSettings)} />
      ),
      LinkedInBusiness: updateAccounts(
        SocialProviderId.LinkedInBusiness,
        linkedinBusinessSettings,
        <LinkedInIcon size="5x" active={!_.isEmpty(linkedinBusinessSettings)} />
      ),
      GoogleAds: updateAccounts(
        SocialProviderId.Google,
        googleSettings,
        <GooglenAnalyticsIcon size="5x" active={!_.isEmpty(googleSettings)} />
      ),
      FacebookAds: updateAccounts(
        SocialProviderId.FacebookAds,
        facebookAdsSettings,
        <FacebookAdsIcon size="5x" active={!_.isEmpty(facebookAdsSettings)} />
      ),
      Gmail: updateAccounts(
        SocialProviderId.Gmail,
        resolveDualScopeSettings(gmailSettings, gmailUserSettings),
        <GmailIcon
          size="5x"
          active={!_.isEmpty(gmailSettings) || !_.isEmpty(gmailUserSettings)}
        />
      ),
      Office365: updateAccounts(
        SocialProviderId.Office365,
        resolveDualScopeSettings(officeSettings, officeUserSettings),
        <Office365Icon
          size="5x"
          active={!_.isEmpty(officeSettings) || !_.isEmpty(officeUserSettings)}
        />
      ),
      Smtp: updateAccounts(
        SocialProviderId.Smtp,
        resolveDualScopeSettings(smtpSettings, smtpUserSettings),
        <SmtpIcon
          size="5x"
          active={!_.isEmpty(smtpSettings) || !_.isEmpty(smtpUserSettings)}
        />
      ),
      WooCommerce: updateAccounts(
        SocialProviderId.WooCommerce,
        wooSettings,
        <WooCommerceIcon size="5x" active={!_.isEmpty(wooSettings)} />
      ),
      Mercadolibre: updateAccounts(
        SocialProviderId.Mercadolibre,
        meliSettings,
        <MercadolibreIcon size="5x" active={!_.isEmpty(meliSettings)} />
      ),
      Jumpseller: updateAccounts(
        SocialProviderId.Jumpseller,
        jumpsellerSettings,
        <JumpsellerIcon size="5x" active={!_.isEmpty(jumpsellerSettings)} />
      ),
      Mailchimp: updateAccounts(
        SocialProviderId.Mailchimp,
        mailchimpSettings,
        <MailchimpIcon size="5x" active={!_.isEmpty(mailchimpSettings)} />
      ),
      Whatsapp: updateAccounts(
        SocialProviderId.Whatsapp,
        whatsappSettings,
        <WhatsappIcon size="5x" active={!_.isEmpty(whatsappSettings)} />
      ),
      OpenAi: updateAccounts(
        SocialProviderId.OpenAi,
        openAiSettings,
        <OpenAiIcon size="5x" active={!_.isEmpty(openAiSettings)} />
      ),
      TikTok: updateAccounts(
        SocialProviderId.TikTok,
        tikTokSettings,
        <TiktokIcon size="5x" active={!_.isEmpty(tikTokSettings)} />
      ),
      ...(isYoutubePostingEnabled()
        ? {
            YouTube: updateAccounts(
              SocialProviderId.YouTube,
              youtubeSettings,
              <YoutubeIcon size="5x" active={!_.isEmpty(youtubeSettings)} />
            ),
          }
        : {}),
      GoogleCalendar: updateAccounts(
        SocialProviderId.GoogleCalendar,
        googleCalendarSettings,
        <GoogleCalendarIcon size="5x" active={!_.isEmpty(googleCalendarSettings)} />
      ),
    }));
  }, [
    facebookSettings,
    facebookAdsSettings,
    instagramSettings,
    instagramLoginSettings,
    resolvedInstagramSettings,
    linkedinSettings,
    linkedinBusinessSettings,
    googleSettings,
    gmailSettings,
    gmailUserSettings,
    officeSettings,
    officeUserSettings,
    smtpSettings,
    smtpUserSettings,
    wooSettings,
    meliSettings,
    jumpsellerSettings,
    mailchimpSettings,
    whatsappSettings,
    openAiSettings,
    tikTokSettings,
    youtubeSettings,
    googleCalendarSettings,
  ]);

  const categories = useMemo(() => {
    const socialItems = [
      "Facebook",
      "Instagram",
      "LinkedIn",
      "LinkedInBusiness",
      "TikTok",
      ...(isYoutubePostingEnabled() ? (["YouTube"] as const) : []),
      "Whatsapp",
    ];

    return {
      "Redes Sociales": {
        icon: faUsers,
        items: socialItems,
      },
      Marketing: {
        icon: faBullhorn,
        items: [
          "FacebookAds",
          "GoogleAds",
          "Mailchimp",
          "WooCommerce",
          "Mercadolibre",
        ],
      },
      "Inteligencia Artificial": {
        icon: faRobot,
        items: ["OpenAi"],
      },
      Comunicaciones: {
        icon: faEnvelope,
        items: ["Gmail", "Office365", "Smtp", "GoogleCalendar"],
      },
      Negocios: {
        icon: faBriefcase,
        items: ["Jumpseller"],
      },
    };
  }, []);

  const handleNetworkSelect = (networkName: string) => {
    setSelectedNetwork(networkName);
    setIntegrationDetailExpanded(true);
  };

  useEffect(() => {
    if (!selectedNetwork) return;
    integrationDetailRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, [selectedNetwork]);

  const getAccountsForNetwork = () => {
    return accounts[selectedNetwork] || [];
  };

  const getNetworkIcon = (network: string) => {
    switch (network) {
      case "Facebook":
        return faFacebook;
      case "Instagram":
        return faInstagram;
      case "Twitter":
        return faTwitter;
      case "TikTok":
        return faTiktok;
      case "YouTube":
        return faYoutube;
      case "LinkedIn":
        return faLinkedin;
      case "FacebookAds":
        return faFacebook;
      case "LinkedInBusiness":
        return faBuilding;
      case "Gmail":
        return faEnvelope;
      case "GoogleCalendar":
        return faGoogleDrive;
      case "Office365":
        return faGoogleDrive;
      case "Smtp":
        return faBoxOpen;
      case "GoogleAds":
        return faGoogle;
      case "WooCommerce":
        return faShoppingCart;
      case "Mercadolibre":
        return faShoppingCart;
      case "Jumpseller":
        return faBoxOpen;
      case "Mailchimp":
        return faMailchimp;
      case "Whatsapp":
        return faWhatsapp;
      case "OpenAi":
        return faSyncAlt;
      default:
        return faLinkedin;
    }
  };

  const getNetworkDisplayName = (network: string) => {
    // Mapeo de nombres especiales que no deben ser formateados automáticamente
    const specialNames: { [key: string]: string } = {
      LinkedIn: "LinkedIn",
      LinkedInBusiness: "LinkedIn Business",
      TikTok: "TikTok",
      YouTube: "YouTube",
      FacebookAds: "Facebook Ads",
      GoogleAds: "Google Ads",
      GoogleCalendar: "Google Calendar",
      Office365: "Office 365",
      OpenAi: "OpenAI",
    };

    if (specialNames[network]) {
      return specialNames[network];
    }

    // Para otros casos, aplicar el formato automático
    return network.replace(/([a-z])([A-Z])/g, "$1 $2");
  };
  const remainingBindDays = useCallback(
    (providerId: SocialProviderId) => {
      const settings = combinedSettings[providerId];

      if (settings && settings.expiresIn) {
        return daysRemainingFromToday(settings.expiresIn);
      }

      return null;
    },
    [combinedSettings]
  );

  const connectedIntegrationCount = useMemo(
    () =>
      Object.keys(accounts).filter(
        (k) => accounts[k] && accounts[k]!.length > 0
      ).length,
    [accounts]
  );

  const totalIntegrationSlots = Object.values(categories).reduce(
    (sum, c) => sum + c.items.length,
    0
  );

  /** `linked` = cuenta vinculada → logo a color; si no, apagado (gris). */
  const renderProviderIcon = (network: string, linked: boolean) => {
    const g = cn("social-hub-tile__glyph", {
      "social-hub-tile__glyph--linked": linked,
      "social-hub-tile__glyph--dim": !linked,
    });
    switch (network) {
      case "Facebook":
        return <FacebookIcon size="2x" active={linked} className={g} />;
      case "Instagram":
        return <InstagramIcon size="2x" active={linked} className={g} />;
      case "LinkedIn":
        return <LinkedInIcon size="2x" active={linked} className={g} />;
      case "Whatsapp":
        return <WhatsappIcon size="2x" active={linked} className={g} />;
      case "FacebookAds":
        return <FacebookAdsIcon size="2x" active={linked} className={g} />;
      case "GoogleAds":
        return <GooglenAnalyticsIcon size="2x" active={linked} className={g} />;
      case "Gmail":
        return <GmailIcon size="2x" active={linked} className={g} />;
      case "Mailchimp":
        return <MailchimpIcon size="2x" active={linked} className={g} />;
      case "Office365":
        return <Office365Icon size="2x" active={linked} className={g} />;
      case "Smtp":
        return <SmtpIcon size="2x" active={linked} className={g} />;
      case "Mercadolibre":
        return <MercadolibreIcon size="2x" active={linked} className={g} />;
      case "WooCommerce":
        return <WooCommerceIcon size="2x" active={linked} className={g} />;
      case "LinkedInBusiness":
        return <LinkedInBusinessIcon size="2x" active={linked} className={g} />;
      case "OpenAi":
        return <OpenAiIcon size="2x" active={linked} className={g} />;
      case "Jumpseller":
        return <JumpsellerIcon size="2x" active={linked} className={g} />;
      case "TikTok":
        return <TiktokIcon size="2x" active={linked} className={g} />;
      case "GoogleCalendar":
        return <GoogleCalendarIcon size="2x" active={linked} className={g} />;
      case "YouTube":
        return <YoutubeIcon size="2x" active={linked} className={g} />;
      default:
        return (
          <FontAwesomeIcon
            icon={getNetworkIcon(network)}
            className={g}
            style={{ fontSize: "1.45rem" }}
          />
        );
    }
  };

  const renderAuthForSelected = () => {
    switch (selectedNetwork) {
      case SocialProviderId.Facebook:
        return <FacebookAuthButton />;
      case SocialProviderId.Instagram:
        return <InstagramConnectionSelector />;
      case SocialProviderId.LinkedIn:
        return <LinkedInAuthButton />;
      case SocialProviderId.LinkedInBusiness:
        return <LinkedInBusinessAuthButton />;
      case SocialProviderId.FacebookAds:
        return <FacebookAdsAuthButton />;
      case SocialProviderId.GoogleAds:
      case SocialProviderId.Google:
        return <GoogleAnalyticsAuthButton />;
      case SocialProviderId.Gmail:
        return <GmailAuthButton />;
      case SocialProviderId.Office365:
        return <Office365AuthButton />;
      case SocialProviderId.Smtp:
        return <SmtpAuthButton />;
      case SocialProviderId.WooCommerce:
        return <WooCommerceAuthButton />;
      case SocialProviderId.Mercadolibre:
        return <MercadolibreAuthButton />;
      case SocialProviderId.Jumpseller:
        return <JumpSellerAuthButton />;
      case SocialProviderId.Mailchimp:
        return <MailchimpAuthButton />;
      case SocialProviderId.Whatsapp:
        return <WhatsappAuthButton />;
      case SocialProviderId.OpenAi:
      case SocialProviderId.OpenAI:
        return <OpenAiAuthButton />;
      case SocialProviderId.TikTok:
        return <TiktokAuthButton />;
      case SocialProviderId.YouTube:
        return <YoutubeAuthButton />;
      default:
        return (
          <button type="button" className="btn btn-outline-secondary" disabled>
            <FontAwesomeIcon icon={faSyncAlt} className="me-2" />
            Integración no disponible
          </button>
        );
    }
  };

  return (
    <PanelWembii className="social-hub social-table container-fluid rounded py-4 px-3 px-md-4">
      <header className="social-hub__hero mb-4">
        <p className="social-hub__eyebrow mb-2">Centro de integraciones</p>
        <div className="d-flex flex-column flex-md-row align-items-start align-items-md-end justify-content-between gap-3">
          <div>
            <h1 className="social-hub__title h3 mb-2 mb-md-1">
              Conecta tus herramientas
            </h1>
            <p className="social-hub__lead text-muted mb-0">
              Unifica redes, marketing, correo y más. Elige un servicio para
              vincular cuentas o revisar el estado de la conexión.
            </p>
          </div>
          <div className="social-hub__stats d-flex gap-2 flex-shrink-0">
            <span className="social-hub__stat-pill">
              <strong>{connectedIntegrationCount}</strong>
              <span className="text-muted"> / {totalIntegrationSlots}</span>
              <span className="d-none d-sm-inline"> activas</span>
            </span>
          </div>
        </div>
      </header>

      {selectedNetwork ? (
        <div
          id="integration-detail-panel"
          ref={integrationDetailRef}
          className="social-hub__detail social-hub__detail--sticky"
        >
          <div
            className={cn("social-hub-detail", {
              "social-hub-detail--collapsed": !integrationDetailExpanded,
            })}
          >
            <div className="social-hub-detail__head">
              <div className="social-hub-detail__head-text">
                <h2 className="social-hub-detail__title h5 mb-1">
                  {getNetworkDisplayName(selectedNetwork)}
                </h2>
                <p className="social-hub-detail__subtitle text-muted small mb-0">
                  {DUAL_SCOPE_INTEGRATIONS.has(selectedNetwork)
                    ? intl.formatMessage({
                        id: "app.integrations.dualScope.subtitle",
                      })
                    : "Cuentas conectadas y acciones de autenticación"}
                </p>
              </div>
              <button
                type="button"
                className="social-hub-detail__toggle"
                onClick={() =>
                  setIntegrationDetailExpanded((open) => !open)
                }
                aria-expanded={integrationDetailExpanded}
                aria-controls={
                  integrationDetailExpanded
                    ? "integration-detail-body"
                    : undefined
                }
                aria-label={
                  integrationDetailExpanded
                    ? intl.formatMessage({
                        id: "app.socialHub.integrationDetail.collapse",
                      })
                    : intl.formatMessage({
                        id: "app.socialHub.integrationDetail.expand",
                      })
                }
                title={
                  integrationDetailExpanded
                    ? intl.formatMessage({
                        id: "app.socialHub.integrationDetail.collapse",
                      })
                    : intl.formatMessage({
                        id: "app.socialHub.integrationDetail.expand",
                      })
                }
              >
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className="social-hub-detail__chevron"
                  aria-hidden
                />
              </button>
            </div>
            {integrationDetailExpanded ? (
              <div
                id="integration-detail-body"
                className="social-hub-detail__body"
              >
              {DUAL_SCOPE_INTEGRATIONS.has(selectedNetwork) ? (
                <ScopedIntegrationDetail
                  provider={
                    selectedNetwork as
                      | "Gmail"
                      | "Office365"
                      | "Smtp"
                      | "GoogleCalendar"
                  }
                />
              ) : getAccountsForNetwork().length > 0 ? (
                <ul className="list-unstyled social-hub-account-list mb-0">
                  {getAccountsForNetwork().map((account, index) => (
                    <li
                      key={index}
                      className="social-hub-account-list__item d-flex flex-column flex-md-row align-items-stretch align-items-md-center justify-content-between gap-3"
                    >
                      <div className="d-flex align-items-center flex-grow-1 social-hub-account-list__main">
                        {account.avatar !== undefined && (
                          <img
                            className="social-hub-account-list__avatar rounded-circle flex-shrink-0"
                            src={account.avatar}
                            loading="lazy"
                            alt=""
                            width={48}
                            height={48}
                          />
                        )}
                        <div className="ms-2 ms-md-3 social-hub-account-list__text">
                          <div className="fw-semibold text-truncate">
                            {account.displayName}
                          </div>
                          {remainingBindDays(account.id as SocialProviderId) !==
                            null &&
                          remainingBindDays(account.id as SocialProviderId)! >
                            0 &&
                          remainingBindDays(account.id as SocialProviderId)! <=
                            365 ? (
                            <div className="small text-muted">
                              Quedan{" "}
                              {remainingBindDays(
                                account.id as SocialProviderId
                              )}{" "}
                              días para reconectar
                            </div>
                          ) : (
                            <div className="small text-muted">
                              Sin fecha de expiración
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="social-hub-account-list__actions flex-shrink-0">
                        {renderAuthForSelected()}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <SyncAccount
                  network={selectedNetwork}
                  existingAccounts={[]}
                  onSync={(newAccount: string): void =>
                    window.location.reload()
                  }
                  onDisconnect={function (account: string): void {
                    window.location.reload();
                  }}
                />
              )}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {Object.entries(categories).map(([category, metadata]) => (
        <section key={category} className="social-hub__section mb-4">
          <h2 className="social-hub__section-title">
            <span className="social-hub__section-icon" aria-hidden>
              <FontAwesomeIcon icon={metadata.icon} />
            </span>
            {category}
          </h2>
          <div className="row g-3">
            {metadata.items.map((network) => {
              const hasAccounts = Boolean(accounts[network]?.length);
              return (
                <div key={network} className="col-12 col-sm-6 col-xl-4">
                  <button
                    type="button"
                    className={cn("social-hub-tile", {
                      "social-hub-tile--active": selectedNetwork === network,
                    })}
                    onClick={() => handleNetworkSelect(network)}
                  >
                    <div
                      className={cn("social-hub-tile__icon-wrap", {
                        "social-hub-tile__icon-wrap--linked": hasAccounts,
                      })}
                    >
                      {renderProviderIcon(network, hasAccounts)}
                    </div>
                    <div className="social-hub-tile__body">
                      <span className="social-hub-tile__name">
                        {getNetworkDisplayName(network)}
                      </span>
                      <span className="social-hub-tile__meta text-muted">
                        {hasAccounts
                          ? accounts[network]!.length > 1
                            ? `${accounts[network]!.length} cuentas`
                            : "1 cuenta vinculada"
                          : "Sin conectar"}
                      </span>
                    </div>
                    <span
                      className={cn("social-hub-tile__badge", {
                        "social-hub-tile__badge--on": hasAccounts,
                      })}
                    >
                      {hasAccounts ? "Activa" : "Pendiente"}
                    </span>
                  </button>
                </div>
              );
            })}
          </div>
        </section>
      ))}

      {anyIsloading ? <LoadingSpinnerWembii active /> : null}
    </PanelWembii>
  );
};

export default SocialTable;
